import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { db, auth, collection, getDocs, limit, query, orderBy, addDoc, doc, updateDoc, deleteDoc } from './firebase';
import { AiFillStar, AiOutlineStar, AiOutlineComment, AiFillEdit, AiFillDelete } from 'react-icons/ai';
import { FaTimes, FaPaperPlane } from 'react-icons/fa';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const Testimonials = () => {
    const [feedbackList, setFeedbackList] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [rating, setRating] = useState(0);
    const [message, setMessage] = useState("");
    const [isAdmin, setIsAdmin] = useState(false);
    const [editingId, setEditingId] = useState(null);

    useEffect(() => {
        const fetchFeedback = async () => {
            try {
                const q = query(
                    collection(db, "feedback"),
                    orderBy("timestamp", "desc"),
                    limit(24)
                );
                const querySnapshot = await getDocs(q);
                const feedbackData = querySnapshot.docs.map((doc) => ({
                    id: doc.id,
                    ...doc.data()
                }));

                if (feedbackData.length === 0) {
                    const placeholders = [
                        { id: 1, name: "Aarav Mehta", message: "Smriti AI completely changed the way I study. I can finally remember key concepts without endless revision.", photo: "https://i.pravatar.cc/150?u=aarav", rating: 5, role: "Student" },
                        { id: 2, name: "Sofia Fernandes", message: "As a working professional, I don't have hours to re-read notes. Smriti AI helps me retain everything faster.", photo: "https://i.pravatar.cc/150?u=sofia", rating: 5, role: "UI Designer" },
                        { id: 3, name: "Rahul Sharma", message: "I used to forget formulas all the time. Now, I recall them instantly thanks to Smriti AI's smart reminders.", photo: "https://i.pravatar.cc/150?u=rahul", rating: 5, role: "Developer" },
                        { id: 4, name: "Emily Chen", message: "This feels like a personal memory coach. My productivity has doubled since I started using Smriti AI.", photo: "https://i.pravatar.cc/150?u=emily", rating: 5, role: "Medical Student" },
                        { id: 5, name: "Kabir Khan", message: "Smriti AI is a game-changer for competitive exam prep. I no longer panic about forgetting important topics.", photo: "https://i.pravatar.cc/150?u=kabir", rating: 5, role: "Aspiration" },
                        { id: 6, name: "Zara Patel", message: "Remembering has never been this easy. Smriti AI makes learning enjoyable and stress-free.", photo: "https://i.pravatar.cc/150?u=zara", rating: 5, role: "Researcher" },
                        { id: 7, name: "John Doe", message: "Absolutely brilliant platform for notes and PYQs!", photo: "https://i.pravatar.cc/150?u=john", rating: 5, role: "Student" },
                        { id: 8, name: "Jane Smith", message: "The video lectures are high quality and very helpful.", photo: "https://i.pravatar.cc/150?u=jane", rating: 5, role: "Student" },
                        { id: 9, name: "Priya Singh", message: "Simplified complex topics perfectly.", photo: "https://i.pravatar.cc/150?u=priya", rating: 5, role: "B.Tech Student" },
                        { id: 10, name: "Vikram Raj", message: "Best resource for IPU students.", photo: "https://i.pravatar.cc/150?u=vikram", rating: 5, role: "CSE Student" },
                        { id: 11, name: "Ananya Iyer", message: "Highly recommend for semester exams.", photo: "https://i.pravatar.cc/150?u=ananya", rating: 5, role: "IT Student" },
                        { id: 12, name: "Siddharth", message: "Saves so much time!", photo: "https://i.pravatar.cc/150?u=sid", rating: 5, role: "Student" }
                    ];
                    setFeedbackList(placeholders);
                } else {
                    setFeedbackList(feedbackData);
                }
            } catch (error) {
                console.error("Error fetching feedback:", error);
            }
        };
        fetchFeedback();
    }, []);

    useEffect(() => {
        const user = auth.currentUser;
        if (user && (user.email === "snaped4338@gmail.com" || user.email === process.env.REACT_APP_ADMIN_EMAIL)) {
            setIsAdmin(true);
        }
    }, []);

    const handleSubmit = async () => {
        if (!rating || !message) {
            toast.error("Please provide rating and message");
            return;
        }

        const user = auth.currentUser;
        if (!user) {
            toast.error("Please sign in to leave feedback");
            return;
        }

        const feedbackData = {
            userId: user.uid,
            name: user.displayName || "Anonymous",
            photo: user.photoURL || "https://via.placeholder.com/50",
            rating,
            message,
            timestamp: new Date(),
        };

        try {
            if (editingId) {
                await updateDoc(doc(db, "feedback", editingId), {
                    rating,
                    message,
                    timestamp: new Date(),
                });
                toast.success("Feedback updated!");
            } else {
                await addDoc(collection(db, "feedback"), feedbackData);
                toast.success("Feedback submitted!");
            }
            setShowModal(false);
            setRating(0);
            setMessage("");
            setEditingId(null);

            const q = query(collection(db, "feedback"), orderBy("timestamp", "desc"), limit(24));
            const querySnapshot = await getDocs(q);
            setFeedbackList(querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        } catch (error) {
            toast.error("Error submitting feedback");
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm("Delete this feedback?")) {
            try {
                await deleteDoc(doc(db, "feedback", id));
                setFeedbackList(prev => prev.filter(f => f.id !== id));
                toast.success("Feedback deleted");
            } catch (error) {
                toast.error("Error deleting feedback");
            }
        }
    };

    const handleEdit = (f) => {
        setEditingId(f.id);
        setRating(f.rating);
        setMessage(f.message);
        setShowModal(true);
    };

    const row1 = feedbackList.slice(0, Math.ceil(feedbackList.length / 2));
    const row2 = feedbackList.slice(Math.ceil(feedbackList.length / 2));

    const MarqueeRow = ({ items, reverse = false }) => (
        <div className="flex overflow-hidden py-2 select-none">
            <motion.div
                className="flex gap-4 px-2 min-w-max"
                animate={{ x: reverse ? [0, "-50%"] : ["-50%", 0] }}
                transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
            >
                {[...items, ...items, ...items].map((item, i) => (
                    <div
                        key={i}
                        className="w-[280px] md:w-[320px] p-5 rounded-xl border backdrop-blur-md flex flex-col transition-colors"
                        style={{ background: 'var(--bg-card)', borderColor: 'var(--border)' }}
                    >
                        <div className="flex items-center gap-3 mb-3">
                            <img src={item.photo || "https://via.placeholder.com/40"} alt="" className="w-10 h-10 rounded-full object-cover border border-green-400/20" />
                            <div>
                                <h4 className="text-sm font-bold leading-tight" style={{ color: 'var(--text-main)' }}>{item.name}</h4>
                                <p className="text-[10px]" style={{ color: 'var(--text-muted)' }}>{item.role || "IPU Student"}</p>
                            </div>
                            <div className="ml-auto flex">
                                {[...Array(5)].map((_, starIdx) => (
                                    <AiFillStar key={starIdx} className={`w-3 h-3 ${starIdx < (item.rating || 5) ? "text-green-400" : "text-gray-700"}`} />
                                ))}
                            </div>
                        </div>
                        <p className="text-xs leading-relaxed line-clamp-3" style={{ color: 'var(--text-main)', opacity: 0.8 }}>"{item.message}"</p>
                        {isAdmin && item.id && (
                            <div className="flex gap-2 mt-3 self-end opacity-0 group-hover:opacity-100 transition-opacity">
                                <button onClick={(e) => { e.stopPropagation(); handleEdit(item); }} className="p-1 text-green-400 hover:bg-green-400/10 rounded"><AiFillEdit size={14} /></button>
                                <button onClick={(e) => { e.stopPropagation(); handleDelete(item.id); }} className="p-1 text-red-400 hover:bg-red-400/10 rounded"><AiFillDelete size={14} /></button>
                            </div>
                        )}
                    </div>
                ))}
            </motion.div>
        </div>
    );

    return (
        <section className="pt-24 pb-12 px-4 overflow-hidden relative" style={{ background: 'var(--bg-main)' }}>
            {/* Background Decorative Elements */}
            <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-green-500/5 blur-[120px] rounded-full pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-cyan-500/5 blur-[120px] rounded-full pointer-events-none" />

            <div className="max-w-7xl mx-auto text-center mb-16 relative z-10">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                >
                    <h2 className="text-4xl md:text-6xl font-bold mb-6 tracking-tight" style={{ color: 'var(--text-main)' }}>
                        Real Voices, <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-cyan-400">Real Impact</span>
                    </h2>
                    <p className="text-base md:text-lg max-w-2xl mx-auto mb-10 leading-relaxed" style={{ color: 'var(--text-muted)' }}>
                        Discover how SnapED helps learners retain knowledge longer and faster. Join thousands of students getting smarter every day.
                    </p>

                    <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => { setShowModal(true); setEditingId(null); }}
                        className="px-8 py-3 rounded-full font-bold flex items-center gap-2 mx-auto transition-colors shadow-lg shadow-green-500/10"
                        style={{ background: 'var(--text-main)', color: 'var(--bg-main)' }}
                    >
                        <AiOutlineComment size={20} /> Share Your Feedback
                    </motion.button>
                </motion.div>
            </div>

            <div className="relative group">
                <MarqueeRow items={row1} />
                <MarqueeRow items={row2} reverse />

                {/* Gradients to fade edges */}
                <div className="absolute inset-y-0 left-0 w-32 z-10 pointer-events-none" style={{ background: 'linear-gradient(to right, var(--bg-main), transparent)' }} />
                <div className="absolute inset-y-0 right-0 w-32 z-10 pointer-events-none" style={{ background: 'linear-gradient(to left, var(--bg-main), transparent)' }} />
            </div>

            <AnimatePresence>
                {showModal && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                        <motion.div
                            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                            className="absolute inset-0 bg-black/80 backdrop-blur-md"
                            onClick={() => setShowModal(false)}
                        />
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0, y: 20 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.9, opacity: 0, y: 20 }}
                            className="relative w-full max-w-md border rounded-3xl p-8 shadow-2xl"
                            style={{ background: 'var(--bg-main)', borderColor: 'var(--border)' }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <button onClick={() => setShowModal(false)} className="absolute top-6 right-6 transition-colors" style={{ color: 'var(--text-muted)' }}><FaTimes size={20} /></button>
                            <h3 className="text-2xl font-bold mb-2" style={{ color: 'var(--text-main)' }}>{editingId ? 'Edit Your Feedback' : 'Rate Your Experience'}</h3>
                            <p className="text-sm mb-6" style={{ color: 'var(--text-muted)' }}>Your feedback helps us build a better platform for everyone.</p>

                            <div className="flex gap-3 mb-8 justify-center">
                                {[1, 2, 3, 4, 5].map(s => (
                                    <button key={s} onClick={() => setRating(s)} className="transition-transform hover:scale-110 active:scale-95">
                                        {s <= rating ? <AiFillStar className="text-green-400" size={36} /> : <AiOutlineStar className="text-gray-700" size={36} />}
                                    </button>
                                ))}
                            </div>

                            <textarea
                                value={message}
                                onChange={e => setMessage(e.target.value)}
                                placeholder="Share your thoughts..."
                                className="w-full h-32 border rounded-2xl p-4 outline-none mb-8 transition-all resize-none"
                                style={{ background: 'var(--bg-card)', borderColor: 'var(--border)', color: 'var(--text-main)' }}
                            />

                            <button
                                onClick={handleSubmit}
                                className="w-full py-4 bg-gradient-to-r from-green-400 to-cyan-400 rounded-2xl text-black font-extrabold flex items-center justify-center gap-2 hover:opacity-90 transition-opacity"
                            >
                                <FaPaperPlane /> {editingId ? 'Update Feedback' : 'Submit Feedback'}
                            </button>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
            <ToastContainer pauseOnFocusLoss={false} />
        </section >
    );
};

export default Testimonials;
